import { useEffect, useRef, useState } from 'react';
import { PosterModal } from '../components/PosterModal';
import { MoreTestsModal } from '../components/MoreTestsModal';
import themeData from '../data/theme-tokens.json';
import { HeroResultCover } from './HeroResultCover';
import { ReportPage } from './ReportPage';
import type { Hero, MatchResult, ThemeName } from '../types';

const verticalTitleHeroIds = new Set([1, 20, 21, 23]);
const lightArtworkHeroIds = new Set([3, 13, 18, 20, 21]);

type Props = {
  hero: Hero;
  result: MatchResult;
  onRestart: () => void;
  initialReport?: boolean;
};

export function ResultPage({ hero, result, onRestart, initialReport = false }: Props) {
  const [posterOpen, setPosterOpen] = useState(false);
  const [moreTestsOpen, setMoreTestsOpen] = useState(false);
  const reportRef = useRef<HTMLElement | null>(null);
  const theme = themeData[hero.theme as ThemeName];
  const style = {
    '--result-primary': theme.primary,
    '--result-secondary': theme.secondary,
    '--result-surface': theme.surface,
    '--result-ink': theme.ink,
    '--result-accent': theme.accent,
    '--theme-primary': theme.primary,
    '--theme-secondary': theme.secondary,
    '--theme-surface': theme.surface,
    '--theme-ink': theme.ink,
    '--theme-accent': theme.accent,
    '--hero-art': `url("${hero.result_bg}")`,
  } as React.CSSProperties;
  const pageClasses = [
    'result-page', 'result-journey',
    verticalTitleHeroIds.has(hero.id) ? 'vertical-title' : '',
    lightArtworkHeroIds.has(hero.id) ? 'light-artwork' : '',
    hero.theme === '危险宿命系' || hero.theme === '铁血将军系' ? 'dark-report' : 'light-report',
  ].filter(Boolean).join(' ');

  useEffect(() => {
    reportRef.current = document.getElementById('report');
    if (!initialReport) return;
    requestAnimationFrame(() => reportRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' }));
  }, [initialReport]);

  function scrollToReport() {
    const report = reportRef.current;
    if (!report) return;
    const top = report.offsetTop;
    requestAnimationFrame(() => window.scrollTo({ top, behavior: 'smooth' }));
  }

  function scrollToCover() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <main className={pageClasses} style={style} data-theme={hero.theme} data-hero-id={hero.id}>
      <HeroResultCover hero={hero} result={result} onSave={() => setPosterOpen(true)} onReport={scrollToReport} />
      <ReportPage hero={hero} onRestart={onRestart} onBackToCover={scrollToCover} onMoreTests={() => setMoreTestsOpen(true)} />
      {posterOpen && <PosterModal hero={hero} result={result} onClose={() => setPosterOpen(false)} />}
      {moreTestsOpen ? <MoreTestsModal onClose={() => setMoreTestsOpen(false)} /> : null}
    </main>
  );
}
